import React, { useEffect, useState } from "react";
import { fetchPublicShare } from "@/lib/api";
import { bodySystems, evidenceElements } from "@/lib/claimData";
import { computePillarStatus } from "@/lib/claimUtils";
import { apptDate, apptStatus, rxNextRefillDate, rxStatus, formatRelativeDay, appointmentTypeMeta } from "@/lib/reminderUtils";
import {
  ShieldCheck, Calendar, Pill, ListChecks, FileText, Tag,
} from "@phosphor-icons/react";

const statusColors = { missing: "status--missing", weak: "status--weak", adequate: "status--adequate", strong: "status--strong" };
const statusLabels = { missing: "Missing", weak: "Weak", adequate: "Adequate", strong: "Strong" };

export default function PublicShareView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchPublicShare(token)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || e.message || "Could not load shared view."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="public-share-shell">
        <div className="loading-screen"><div className="loading-logo">VA</div><div className="loading-text">CLAIMOPS VA</div><div className="loading-sub">Loading shared view...</div></div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="public-share-shell">
        <div className="public-share-error" data-testid="share-error-screen">
          <ShieldCheck size={36} weight="duotone" />
          <h2>This share link is not available</h2>
          <p>{err || "The link may have been revoked or has expired."}</p>
        </div>
      </div>
    );
  }

  const allIssues = [];
  const allEvidence = [];
  const allStatements = [];
  const issueIdToCtx = {};
  for (const cd of (data.claimData || [])) {
    const ctx = { workspace: cd.workspace, master526ez: cd.master526ez };
    (cd.issues || []).forEach((i) => { allIssues.push(i); issueIdToCtx[i.id] = ctx; });
    (cd.evidence || []).forEach((e) => allEvidence.push(e));
    (cd.statements || []).forEach((s) => allStatements.push(s));
  }

  // Roll up readiness honoring per-workspace nexus waivers.
  const readiness = (() => {
    if (!allIssues.length) return 0;
    let haveSum = 0;
    const total = allIssues.length * evidenceElements.length;
    allIssues.forEach((issue) => {
      const ctx = issueIdToCtx[issue.id] || {};
      haveSum += evidenceElements
        .filter((el) => computePillarStatus(issue, el.id, allEvidence, ctx) === "have").length;
    });
    return Math.round((haveSum / Math.max(1, total)) * 100);
  })();

  return (
    <div className="public-share-shell" data-testid="public-share-view">
      <header className="public-share-header">
        <div className="public-share-brand">
          <div className="brand-mark">VA</div>
          <div>
            <div className="brand-name">CLAIMOPS</div>
            <div className="brand-sub">Read-only shared view</div>
          </div>
        </div>
        <div className="public-share-meta">
          <div className="public-share-label">{data.label}</div>
          <div className="public-share-sub">Shared by <strong>{data.owner.displayName}</strong></div>
        </div>
        <div className="public-share-readonly">
          <ShieldCheck size={14} weight="bold" /> READ-ONLY
        </div>
      </header>

      <div className="public-share-body">
        {/* Summary stats */}
        <div className="panel">
          <div className="panel-header">
            <FileText size={18} weight="bold" />
            <span>SUMMARY</span>
          </div>
          <div className="panel-body">
            <div className="public-stats-grid">
              <div className="public-stat"><div className="public-stat-val mono">{allIssues.length}</div><div className="public-stat-lbl">Conditions</div></div>
              <div className="public-stat"><div className="public-stat-val mono">{allEvidence.length}</div><div className="public-stat-lbl">Evidence items</div></div>
              <div className="public-stat"><div className="public-stat-val mono">{readiness}%</div><div className="public-stat-lbl">Readiness</div></div>
              <div className="public-stat"><div className="public-stat-val mono">{(data.appointments || []).filter((a) => !a.completed).length}</div><div className="public-stat-lbl">Upcoming appts</div></div>
              <div className="public-stat"><div className="public-stat-val mono">{(data.prescriptions || []).filter((r) => !r.archived).length}</div><div className="public-stat-lbl">Prescriptions</div></div>
              <div className="public-stat"><div className="public-stat-val mono">{allStatements.length}</div><div className="public-stat-lbl">Statements</div></div>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div className="panel">
          <div className="panel-header"><ListChecks size={18} weight="bold" /><span>CONDITIONS & EVIDENCE</span><span className="header-count mono">{allIssues.length}</span></div>
          <div className="panel-body">
            {allIssues.length === 0 && <div className="empty-state">No conditions tracked yet.</div>}
            {allIssues.map((issue) => (
              <div key={issue.id} className="public-issue-row" data-testid={`public-issue-${issue.id}`}>
                <div className="public-issue-head">
                  <div>
                    <div className="mono small" style={{ color: "#64748B" }}>DC {issue.code}</div>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{issue.claimedAs}</div>
                    <div className="small"><Tag size={11} weight="bold" /> {bodySystems.find((s) => s.id === issue.system)?.label || issue.system}</div>
                  </div>
                  <div className="public-issue-meta">
                    {issue.priority && <span className="role-pill">{issue.priority}</span>}
                    {issue.estimatedRating ? <span className="role-pill role-pill--admin">{issue.estimatedRating}%</span> : null}
                  </div>
                </div>
                {issue.functionalImpact && <div className="small" style={{ marginTop: 6 }}>{issue.functionalImpact}</div>}
                <div className="evidence-grid" style={{ marginTop: 10 }}>
                  {evidenceElements.map((el) => {
                    const ctx = issueIdToCtx[issue.id] || {};
                    const status = computePillarStatus(issue, el.id, allEvidence, ctx);
                    const count = allEvidence.filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === el.id).length;
                    return (
                      <div key={el.id} className="evidence-cell">
                        <span className="evidence-cell-label">{el.label}</span>
                        <span className={`evidence-pill mono ${statusColors[status]}`}>
                          {statusLabels[status]}{count > 0 && <span className="ev-pill-count"> · {count}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Appointments */}
        <div className="panel">
          <div className="panel-header"><Calendar size={18} weight="bold" /><span>APPOINTMENTS</span><span className="header-count mono">{(data.appointments || []).length}</span></div>
          <div className="panel-body" style={{ padding: 0 }}>
            {(data.appointments || []).length === 0 ? (
              <div className="admin-empty">No appointments shared.</div>
            ) : (
              <div className="table-scroll">
              <table className="admin-table table--cards-on-mobile">
                <thead><tr><th>WHEN</th><th>TYPE</th><th>TITLE</th><th>PROVIDER</th><th>STATUS</th></tr></thead>
                <tbody>
                  {(data.appointments || []).map((a) => {
                    const d = apptDate(a);
                    const meta = appointmentTypeMeta(a.type);
                    const status = apptStatus(a);
                    return (
                      <tr key={a.id}>
                        <td data-label="WHEN"><div className="mono">{d ? d.toLocaleDateString() : "—"}</div><div className="small">{d ? formatRelativeDay(d) : ""}</div></td>
                        <td data-label="TYPE"><span className="appt-type-pill" style={{ background: meta.color }}>{meta.label}</span></td>
                        <td data-label="TITLE" style={{ fontWeight: 700 }}>{a.title}</td>
                        <td data-label="PROVIDER" className="small">{a.provider || "—"}</td>
                        <td data-label="STATUS"><span className={`rem-status rem-status--${status === "ok" ? "ok" : status === "completed" ? "completed" : status === "due_soon" ? "due" : "overdue"}`}>{status.toUpperCase().replace("_", " ")}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* Prescriptions */}
        <div className="panel">
          <div className="panel-header"><Pill size={18} weight="bold" /><span>PRESCRIPTIONS</span><span className="header-count mono">{(data.prescriptions || []).filter((r) => !r.archived).length}</span></div>
          <div className="panel-body" style={{ padding: 0 }}>
            {(data.prescriptions || []).filter((r) => !r.archived).length === 0 ? (
              <div className="admin-empty">No prescriptions shared.</div>
            ) : (
              <div className="table-scroll">
              <table className="admin-table table--cards-on-mobile">
                <thead><tr><th>MEDICATION</th><th>DOSAGE</th><th>NEXT REFILL</th><th>STATUS</th></tr></thead>
                <tbody>
                  {(data.prescriptions || []).filter((r) => !r.archived).map((rx) => {
                    const next = rxNextRefillDate(rx);
                    const status = rxStatus(rx);
                    return (
                      <tr key={rx.id}>
                        <td data-label="MEDICATION"><div style={{ fontWeight: 700 }}>{rx.medName}</div><div className="small">{rx.prescriber || ""}</div></td>
                        <td data-label="DOSAGE">{rx.dosage || "—"}</td>
                        <td data-label="NEXT REFILL" className="mono small">{next ? next.toLocaleDateString() : "—"}</td>
                        <td data-label="STATUS"><span className={`rem-status rem-status--${status === "ok" ? "ok" : status === "due_soon" ? "due" : "overdue"}`}>{status.toUpperCase().replace("_", " ")}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* Statements */}
        {allStatements.length > 0 && (
          <div className="panel">
            <div className="panel-header"><FileText size={18} weight="bold" /><span>STATEMENTS</span><span className="header-count mono">{allStatements.length}</span></div>
            <div className="panel-body">
              {allStatements.map((s, i) => (
                <div key={s.id || i} className="public-statement">
                  <div className="public-statement-head">
                    <span className="role-pill role-pill--admin">{s.statementType || s.type || "Statement"}</span>
                    {s.title && <span style={{ fontWeight: 700 }}>{s.title}</span>}
                  </div>
                  {s.body && <pre className="public-statement-body">{s.body}</pre>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="public-share-footer">
          <ShieldCheck size={12} weight="bold" />
          <span>This shared view contains read-only data prepared by the veteran. It is NOT an official VA document and contains no PII like SSN or full email.</span>
        </div>
      </div>
    </div>
  );
}
